import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import '../../styles/user/Report.css';
import { fetchCandidateInfo } from '../../services/user/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import PdfPreviewModal from './PdfPreviewModal';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const ReportPage = ({ candidateId , onBack}) => {
  const [candidateData, setCandidateData] = useState(null);
 const navigate = useNavigate();
  const location = useLocation();
const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    fetchCandidateInfo(candidateId)
      .then(res => {
        if (res.data.status) {
          setCandidateData(res.data.data);

        }
      })
      .catch(error => console.error('Error fetching candidate info:', error));
  }, [candidateId]);

  const InputGroup = ({ label, type = 'text', value = '', placeholder = '', className = '' }) => (
    <div className={`report-input-group ${className}`}>
      <label>{label}</label>
      <input type={type} value={value} placeholder={placeholder} readOnly />
    </div>
  );

const handleBack = () => {
  if (onBack) {
    onBack();
  } else {
    navigate(-1);
  }
};

  const ImageUploadBox = ({ title, imageUrl }) => {
  return (
    <div className="report-image-upload-box">
      <div className="report-image-upload-header">
        <h4>{title}</h4>

        {imageUrl && (
          <button
            className="report-add-button"
            onClick={() => setPreviewFile(imageUrl)}
          >
            <Plus size={16} /> Preview
          </button>
        )}

      </div>

      <div className="report-image-display">
        {!imageUrl && <p>No document uploaded</p>}
        {imageUrl && !imageUrl.endsWith('.pdf') && (
          <img src={imageUrl} alt={title} className="report-uploaded-image" />
        )}
      </div>
    </div>
  );
};

  if (!candidateData) return  <div className="centered-message">
    <div className="spinner"></div>
    <div>Loading candidate report...</div>
  </div>;

const { personalInfoResponse, addressInfoResponse, educationInfos, employmentInfos } = candidateData;



  const getEducation = (level) =>
    educationInfos?.find((edu) => edu.educationLevel === level) || {};

  return (
    <div className="report-page-container">

       <div className="view-report-back">
        <button className="view-report-back-button" onClick={handleBack}>
          ← Back
        </button>
      </div>

      <h1 className="view-report-main-title">Candidate Report</h1>

      {/* Personal Info */}
      <div className="view-report-section">
        <h3 className="report-section-title">Personal Details</h3>
        <div className="report-section-grid">
          <InputGroup label="First Name" value={personalInfoResponse.firstName} />
          <InputGroup label="Last Name" value={personalInfoResponse.lastName} />
          <InputGroup label="Father Name" value={personalInfoResponse.fatherName} />
          <InputGroup label="Candidate Email" value={personalInfoResponse.email} />
          <InputGroup label="Phone Number" value={personalInfoResponse.mobileNumber} />
          <InputGroup label="Date Of Birth" value={personalInfoResponse.dateOfBirth}  />
          <InputGroup label="Gender" value={personalInfoResponse.gender} />
          <InputGroup label="Aadhaar Card Number" value={personalInfoResponse.aadhaarCardNumber} />
        </div>
      </div>

      {/* Permanent Address */}
      <div className="view-report-section">
        <h3 className="report-section-title">Permanent Address Details</h3>
        <div className="report-section-grid">
          <InputGroup label="Address Line 1" value={addressInfoResponse.permanentAddressLine1} />
          <InputGroup label="Address Line 2" value={addressInfoResponse.permanentAddressLine2} />
          <InputGroup label="State" value={addressInfoResponse.permanentState} />
          <InputGroup label="Country" value={addressInfoResponse.permanentCountry} />
          <InputGroup label="Pin Code" value={addressInfoResponse.permanentPinCode} />
        </div>
        <div className="report-section-single-column">
          <ImageUploadBox title="Permanent Address Proof" imageUrl={addressInfoResponse.permanentAddressFile} />
        </div>
      </div>

      {/* Current Address */}
      <div className="view-report-section">
        <h3 className="report-section-title">Current Address Details</h3>
        <div className="report-section-grid">
          <InputGroup label="Address Line 1" value={addressInfoResponse.currentAddressLine1} />
          <InputGroup label="Address Line 2" value={addressInfoResponse.currentAddressLine2} />
          <InputGroup label="State" value={addressInfoResponse.currentState} />
          <InputGroup label="Country" value={addressInfoResponse.currentCountry} />
          <InputGroup label="Pin Code" value={addressInfoResponse.currentPinCode} />
        </div>
        <div className="report-section-single-column">
          <ImageUploadBox title="Current Address Proof" imageUrl={addressInfoResponse.currentAddressFile} />
        </div>
      </div>

      {/* Education Sections */}
      {["HIGH_SCHOOL", "HIGHER_SECONDARY", "DIPLOMA", "UNDERGRADUATE", "POSTGRADUATE"].map(level => {
        const edu = getEducation(level);
        if (!edu || !edu.enrollmentNumber) return null;

        return (
          <div className="view-report-section" key={level}>
            <h3 className="report-section-title">{level.replace('_', ' ')} Details</h3>
            <div className="report-section-grid">
              <InputGroup label="Enrollment No" value={edu.enrollmentNumber} />
              <InputGroup label="Qualification" value={edu.qualification} />
              <InputGroup label="Institute" value={edu.instituteName} />
              <InputGroup label="Specialization" value={edu.specialization} />
              <InputGroup label="Enrolled Year" value={edu.enrolledYear} />
              <InputGroup label="Year Of Passing" value={edu.passingYear} />
              <InputGroup label="Address" value={edu.address} />
              <InputGroup label="City" value={edu.city} />
              <InputGroup label="State" value={edu.stateRegionProvince} />
              <InputGroup label="ZipCode" value={edu.zipCode} />
              <InputGroup label="Country" value={edu.country} />
            </div>
            <div className="report-section-single-column">
              <ImageUploadBox title={`${level.replace('_', ' ')} Document`} imageUrl={edu.certificateFileUrl} />
            </div>
          </div>
        );
      })}

      {/* Employment Details */}
{employmentInfos?.map((emp, index) => (
  <div className="view-report-section" key={index}>
    <h3 className="report-section-title">Employment Details - {index + 1}</h3>
    <div className="report-section-grid">
      <InputGroup label="Employee Code" value={emp.employeeCode} />
      <InputGroup label="Company Name" value={emp.companyName} />
      <InputGroup label="UAN Number" value={emp.uanNumber} />
      <InputGroup label="Job Title" value={emp.jobTitle} />
      <InputGroup label="Location" value={emp.location} />
      <InputGroup label="Working From" value={emp.workingFrom} />
      <InputGroup label="Working To" value={emp.workingTo} />
      <InputGroup label="HR Contact Number" value={emp.hrContactNumber} />
      <InputGroup label="HR Contact Email" value={emp.hrContactEmail} />
      <InputGroup label="Employer Reference Number" value={emp.employerReferenceNumber} />
    </div>

    <div className="report-section-grid">
      <ImageUploadBox title="Offer Letter" imageUrl={emp.offerLetterUrl} />
      <ImageUploadBox title="Experience Letter" imageUrl={emp.experienceLetterUrl} />
      <ImageUploadBox title="Form 16" imageUrl={emp.form16Url} />
      <ImageUploadBox title="Form 26" imageUrl={emp.form26Url} />
      <ImageUploadBox title="Bank Statement" imageUrl={emp.bankStatementUrl} />
    </div>

    {/* Multiple salary slips */}
    {emp.salarySlipsUrl?.split(',').map((url, i) => (
      <ImageUploadBox key={i} title={`Salary Slip ${i + 1}`} imageUrl={url.trim()} />
    ))}
  </div>
))}

{previewFile && (
  <PdfPreviewModal
    fileUrl={previewFile}
    onClose={() => setPreviewFile(null)}
  />
)}

    </div>
  );
};

export default ReportPage;
